import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { ColumnApi, GridApi, GridReadyEvent, PaginationChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AG_GRID_LOCALE_EN } from '../../simple-table/ag-grid.locale.en';
import { AG_GRID_LOCALE_NBNO } from '../../simple-table/ag-grid.locale.nb';
import { RadioFilterOpts, TableConfig } from '../../simple-table/simple-table';
import { iTableSwitch } from '../../table-filters/table-filters';
import TableToolBar from '../../table-tool-bar/table-tool-bar';
import { handleSaveTable } from '../../table-helpers/table-helpers';
import { TableProductsFilterProps } from '../../table-products-filter/table-products-filter';
import TablePaginationMui from '../table-pagination/table-pagination';

/* eslint-disable-next-line */
export interface FocusTableProps {
  data: any[],
  headers: any[],
  dateRange?: DateRange<Date>,
  onSetDateRange?: (dateRange: DateRange<Date> | null) => void,
  onSelectionChanged?: (...args: any[]) => void,
  pageSize?: number,
  disableSearch?: boolean,
  tableFilters?: iTableSwitch[],
  toggleFilters?: RadioFilterOpts,
  tableConfig?: TableConfig,
  expandComponent?: any,
  expandProps?: any,
  productsFilter?: TableProductsFilterProps
  isLoading?: boolean;
  onRefresh?: () => void;
  hidePagination?: boolean,
  useCustomPagination?: boolean,
  singleCellSelection?: boolean,
  allSelected?: boolean,
  onSelectAll?: (...args: any[]) => void
}
export function FocusTable(props: FocusTableProps) {
  if (props.headers.length === 0) return null;
  return <FocusTableContent {...props} />;
}
function FocusTableContent(props: FocusTableProps) {
  const { t, i18n } = useTranslation();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [quickFilter, setQuickFilter] = useState<string>("");
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [rowCount, setRowCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(props.pageSize || 10);

  const createSummaryRow = () => {
    const summaryRow: any = {};
    const filteredRows: any[] = [];

    gridApi?.forEachNodeAfterFilter(node => filteredRows.push(node.data));
    props.headers.forEach(h => {
      summaryRow[h.field] = h.summary ? h.summary(h, filteredRows) : null;
    });
    summaryRow["isSummary"] = true;
    return [summaryRow];
  }
  const [summaryRow, setSummaryRow] = useState<any[]>([]);
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };
  const columnDefinition = {
    resizable: true,
    sortable: true,
  }
  useEffect(() => {
    if (gridApi) {
      setSummaryRow(createSummaryRow());

      gridApi.setFilterModel(props.tableConfig?.filterModel);
      gridApi.setQuickFilter(quickFilter);
    }
  }, [quickFilter, gridApi, props.data, props.tableConfig]);

  let headers = props.headers;
  headers = headers.map(h => {
    const columnDefinition = props.tableConfig?.columns && props.tableConfig?.columns?.find(c => c.columnName === h.field);
    if (columnDefinition) {
      h.hide = columnDefinition.isHidden;
      h.sort = columnDefinition.sortDirection;
    }
    return h;
  });

  const onSelectionChanged = () => {
    const selectedRows = gridApi?.getSelectedRows();
    props.onSelectionChanged?.(selectedRows);
  };

  const gridPaginationChanged = (e?: PaginationChangedEvent) => {
    setCurrentPage((gridApi?.paginationGetCurrentPage() || 0) + 1);
    setPageCount(gridApi?.paginationGetTotalPages() || 1);
    setRowCount(gridApi?.getDisplayedRowCount() || 0);
  };

  const changePageSize = (e: any) => {
    setPageSize(e.target.value);
    gridApi?.paginationSetPageSize(e.target.value);
  };

  const getContextMenuItems = useCallback((params: any) => {
    const getContextMenuItems: any[] = ["copy", "export"];
    const tableConfig = props.tableConfig;
    if (tableConfig) {
      getContextMenuItems.push({
        name: t("simpleTable.saveTableConfiguration"),
        action: () => handleSaveTable(params, tableConfig)
      });
      getContextMenuItems.push({
        name: t("simpleTable.resetTableConfiguration"),
        action: () => tableConfig?.onSaveTableConfig(null, null)
      });
    }
    return getContextMenuItems;
  }, []);
  props.isLoading ?
    gridApi?.showLoadingOverlay() : gridApi?.hideOverlay();

  return (
    <div className="ag-theme-alpine" data-testid="ag-theme-alpine-focus-table">
      <TableToolBar
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        gridApi={gridApi}
        {...props}
      />
      <AgGridReact
        localeText={i18n.language === 'nb' ? AG_GRID_LOCALE_NBNO : AG_GRID_LOCALE_EN}
        rowData={props.data}
        columnDefs={headers}
        getContextMenuItems={getContextMenuItems}
        onFilterChanged={() => setSummaryRow(createSummaryRow())}
        onGridReady={onGridReady}
        rowSelection={props.singleCellSelection ? 'false' : 'single'}
        onSelectionChanged={onSelectionChanged}
        rowHeight={50}
        domLayout={'autoHeight'}
        defaultColDef={columnDefinition}
        pagination={props.hidePagination ? false : true}
        suppressPaginationPanel={props.useCustomPagination ? true : false}
        onPaginationChanged={gridPaginationChanged}
        masterDetail={true}
        pinnedBottomRowData={headers.some(h => h.summary) ? summaryRow : undefined}
        paginationPageSize={pageSize}
        detailCellRenderer={props.expandComponent}
        detailCellRendererParams={props.expandProps}
        detailRowAutoHeight={true}
      />
      {props.useCustomPagination &&
        <TablePaginationMui
          pageSize={pageSize}
          onChangePageSize={changePageSize}
          gridApi={gridApi}
          currentPage={currentPage}
          rowCount={rowCount}
          pageCount={pageCount} />
      }
    </div>
  );
}

export default FocusTable;